import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Spinner, Table } from "reactstrap";

import { useToast } from "hooks/toast";
import { listRoles } from "services/rolesApi";
import { convidarUsuarios, listConvites } from "services/onboardingApi";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const REASON_LABEL = {
  empty: "e-mail vazio",
  invalid: "e-mail inválido",
  user_exists: "usuário já existe",
  storage_error: "falha de armazenamento",
  email_failed: "falha no envio do e-mail",
};

// Usuários do embarcador — PO1-2428. Reuso do convite PO1-2046: cada
// convidado recebe link único (48h) e define a própria senha no aceite.
// linhasIniciais: pré-preenchimento vindo da planilha padrão (aba Usuário) —
// convites SÓ saem quando o operador revisa os papéis e clica Enviar.
export default function UsuariosStep({ idEmbarcador, linhasIniciais, onChanged }) {
  const { addToast } = useToast();
  const [linhas, setLinhas] = useState(() =>
    linhasIniciais && linhasIniciais.length
      ? linhasIniciais.map((u) => ({
          nome: u.nome || "",
          email: u.email || "",
          roleId: "",
        }))
      : [{ nome: "", email: "", roleId: "" }]
  );
  const [roles, setRoles] = useState([]);
  const [convites, setConvites] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const carregarConvites = useCallback(async () => {
    try {
      const data = await listConvites(idEmbarcador);
      setConvites(data.convites || []);
    } catch (err) {
      console.error("[UsuariosStep] listConvites:", err);
    }
  }, [idEmbarcador]);

  useEffect(() => {
    (async () => {
      try {
        const list = await listRoles();
        setRoles(Array.isArray(list) ? list : list.items || []);
      } catch (err) {
        console.error("[UsuariosStep] listRoles:", err);
      }
    })();
    carregarConvites();
  }, [carregarConvites]);

  const setLinha = useCallback((index, campo, valor) => {
    setLinhas((prev) =>
      prev.map((linha, i) => (i === index ? { ...linha, [campo]: valor } : linha))
    );
  }, []);

  const addLinha = useCallback(() => {
    setLinhas((prev) => [...prev, { nome: "", email: "", roleId: "" }]);
  }, []);

  const rmLinha = useCallback((index) => {
    setLinhas((prev) =>
      prev.length > 1 ? prev.filter((_l, i) => i !== index) : prev
    );
  }, []);

  const handleEnviar = useCallback(async () => {
    const preenchidas = linhas.filter(
      (linha) => linha.email.trim() || linha.nome.trim()
    );
    if (preenchidas.length === 0) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Informe ao menos um usuário",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    const invalidas = preenchidas.filter(
      (linha) => !EMAIL_REGEX.test(linha.email.trim())
    );
    if (invalidas.length) {
      addToast({
        place: "tr",
        type: "danger",
        title: "E-mail inválido",
        description: invalidas.map((linha) => linha.email || "(vazio)").join(", "),
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }

    setEnviando(true);
    try {
      const resultado = await convidarUsuarios(
        idEmbarcador,
        preenchidas.map((linha) => ({
          nome: linha.nome.trim(),
          email: linha.email.trim(),
          roleId: linha.roleId ? Number(linha.roleId) : undefined,
        }))
      );
      const created = (resultado.created || []).length;
      const skipped = resultado.skipped || [];
      if (created) {
        addToast({
          place: "tr",
          type: "success",
          title: `${created} convite${created > 1 ? "s" : ""} enviado${created > 1 ? "s" : ""}`,
          icon: "tim-icons icon-email-85",
        });
        setLinhas([{ nome: "", email: "", roleId: "" }]);
      }
      if (skipped.length) {
        addToast({
          place: "tr",
          type: created ? "warning" : "danger",
          title: `${skipped.length} convite${skipped.length > 1 ? "s" : ""} não enviado${skipped.length > 1 ? "s" : ""}`,
          description: skipped
            .map((s) => `${s.email}: ${REASON_LABEL[s.reason] || s.reason}`)
            .join("\n"),
          icon: "tim-icons icon-alert-circle-exc",
        });
      }
      await carregarConvites();
      onChanged();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao enviar convites",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setEnviando(false);
    }
  }, [linhas, idEmbarcador, addToast, carregarConvites, onChanged]);

  return (
    <div>
      <Table borderless size="sm">
        <thead>
          <tr>
            <th style={{ width: "30%" }}>Nome</th>
            <th style={{ width: "38%" }}>Email (login)</th>
            <th style={{ width: "26%" }}>Papel</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index}>
              <td>
                <Input
                  value={linha.nome}
                  onChange={(e) => setLinha(index, "nome", e.target.value)}
                  placeholder="Nome"
                />
              </td>
              <td>
                <Input
                  value={linha.email}
                  onChange={(e) => setLinha(index, "email", e.target.value)}
                  placeholder="email@empresa.com"
                />
              </td>
              <td>
                <Input
                  type="select"
                  value={linha.roleId}
                  onChange={(e) => setLinha(index, "roleId", e.target.value)}
                >
                  <option value="">Sem papel</option>
                  {roles.map((role) => (
                    <option key={role.Id} value={role.Id}>
                      {role.DisplayName || role.Name}
                    </option>
                  ))}
                </Input>
              </td>
              <td>
                <Button close title="remover" onClick={() => rmLinha(index)} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button color="link" size="sm" onClick={addLinha}>
        + adicionar usuário
      </Button>

      <div className="d-flex justify-content-end mt-2">
        <Button color="primary" onClick={handleEnviar} disabled={enviando}>
          {enviando ? <Spinner size="sm" /> : "Enviar convites"}
        </Button>
      </div>

      <div className="onb-info">
        <span role="img" aria-label="informação">ℹ️</span>
        <span>
          <b>Senha:</b> cada usuário recebe um e-mail com link único (válido
          48h) para definir a própria senha. Reusa o convite do Hub (PO1-2046).
        </span>
      </div>

      {convites.length > 0 && (
        <div className="mt-3">
          <h5 style={{ fontSize: 14 }}>Convites pendentes</h5>
          {convites.map((convite) => (
            <div className="onb-filial" key={convite.jti}>
              <div>
                <div className="nm">{convite.nome || convite.email}</div>
                <div className="ci">{convite.email}</div>
              </div>
              <span className="ci">
                expira{" "}
                {convite.expiresAt
                  ? new Date(convite.expiresAt).toLocaleString("pt-BR")
                  : "-"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
